import React, { useEffect, useState } from 'react';
import useApi from '../../api';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Loader2, Eye, User, Building2, Handshake, History } from 'lucide-react';
import { Checkbox } from "@/components/ui/checkbox";
import { CRM_ACTIVITY_TYPES, CRM_ACTIVITY_PROVIDERS } from '../tabs/crmTools';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

export interface CrmReadField {
    key: string;
    property: string;
    object: string;
    label: string;
    sayable: boolean;
}

export const MAX_READ_FIELDS = 30;

interface ObjectOption {
    value: string;   // stored in config: 'contact' | 'company' | 'account' | 'lead'
    api: string;     // ?object= value for the contact-fields endpoint
    label: string;
}

const PROVIDER_OBJECTS: Record<string, ObjectOption[]> = {
    hubspot: [
        { value: 'contact', api: 'contacts', label: 'Contact' },
        { value: 'company', api: 'companies', label: 'Company' },
    ],
    salesforce: [
        { value: 'contact', api: 'Contact', label: 'Contact' },
        { value: 'account', api: 'Account', label: 'Account' },
        { value: 'lead', api: 'Lead', label: 'Lead' },
    ],
};

// Field objects behind each tool. Mirrors the backend's CONTACT_OBJECTS /
// ACCOUNT_OBJECTS split in crm_read_service.py.
const CONTACT_GROUP = ['contact', 'lead'];
const ACCOUNT_GROUP = ['company', 'account'];

const slugifyKey = (label: string) =>
    label
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 40);

interface CrmReadFieldsSectionProps {
    integrationId: string;
    provider: 'hubspot' | 'salesforce';
    readFields: CrmReadField[];
    setReadFields: (fields: CrmReadField[]) => void;
    aiTools: string[];
    setAiTools: (tools: string[]) => void;
    activityTypes?: string[];
    setActivityTypes?: (types: string[]) => void;
    renderPropertySelect: (props: {
        fields: any[];
        value?: string;
        onSelect: (value: string) => void;
    }) => React.ReactNode;
}

export function CrmReadFieldsSection({
    integrationId,
    provider,
    readFields,
    setReadFields,
    aiTools,
    setAiTools,
    activityTypes = [],
    setActivityTypes,
    renderPropertySelect,
}: CrmReadFieldsSectionProps) {
    const { get } = useApi();
    const objects = PROVIDER_OBJECTS[provider];
    const [fieldsByObject, setFieldsByObject] = useState<Record<string, any[]>>({});
    const [loadingObjects, setLoadingObjects] = useState<Record<string, boolean>>({});

    const ensureObjectFields = async (objectValue: string) => {
        if (fieldsByObject[objectValue] || loadingObjects[objectValue]) return;
        const option = objects.find(o => o.value === objectValue);
        if (!option) return;
        setLoadingObjects(prev => ({ ...prev, [objectValue]: true }));
        try {
            const response = await get(
                `/integrations/${integrationId}/contact-fields?mode=read&object=${encodeURIComponent(option.api)}`
            );
            if (response.data.status === 'success') {
                setFieldsByObject(prev => ({ ...prev, [objectValue]: response.data.fields || [] }));
            }
        } catch (err) {
            console.error(`Failed to load ${provider} ${objectValue} properties:`, err);
        } finally {
            setLoadingObjects(prev => ({ ...prev, [objectValue]: false }));
        }
    };

    useEffect(() => {
        // Preload property lists for objects already used by saved mappings,
        // plus the default object of each tool group.
        const used = new Set<string>([
            ...objects.map(o => o.value).filter(v => CONTACT_GROUP.includes(v)).slice(0, 1),
            ...readFields.map(f => f.object),
        ]);
        used.forEach(objectValue => { void ensureObjectFields(objectValue); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [integrationId]);

    const toolEnabled = (kind: string) => aiTools.includes(kind);
    const setToolEnabled = (kind: string, enabled: boolean) => {
        if (enabled) {
            setAiTools(aiTools.includes(kind) ? aiTools : [...aiTools, kind]);
        } else {
            setAiTools(aiTools.filter(k => k !== kind));
        }
    };

    const updateField = (index: number, patch: Partial<CrmReadField>) => {
        const next = readFields.map((field, i) => (i === index ? { ...field, ...patch } : field));
        setReadFields(next);
    };

    const handlePropertySelect = (index: number, propertyName: string) => {
        const field = readFields[index];
        const objectFields = fieldsByObject[field.object] || [];
        const selected = objectFields.find((f: any) => f.name === propertyName);
        const label = selected?.label || propertyName;
        updateField(index, {
            property: propertyName,
            label,
            key: field.key || slugifyKey(label),
        });
    };

    const handleObjectChange = (index: number, objectValue: string) => {
        void ensureObjectFields(objectValue);
        updateField(index, { object: objectValue, property: '', label: '', key: '' });
    };

    const addRow = (group: string[]) => {
        if (readFields.length >= MAX_READ_FIELDS) return;
        const defaultObject = objects.find(o => group.includes(o.value))?.value || group[0];
        void ensureObjectFields(defaultObject);
        setReadFields([
            ...readFields,
            { key: '', property: '', object: defaultObject, label: '', sayable: false },
        ]);
    };

    const removeRow = (index: number) => {
        setReadFields(readFields.filter((_, i) => i !== index));
    };

    const renderFieldRow = (field: CrmReadField, index: number, group: string[]) => {
        const groupObjects = objects.filter(o => group.includes(o.value));
        return (
            <div key={index} className="rounded-lg border p-3 space-y-3">
                <div className="flex items-center gap-2">
                    {groupObjects.length > 1 ? (
                        <Select value={field.object} onValueChange={(v) => handleObjectChange(index, v)}>
                            <SelectTrigger className="w-32 shrink-0">
                                <SelectValue placeholder="Object" />
                            </SelectTrigger>
                            <SelectContent>
                                {groupObjects.map(o => (
                                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    ) : (
                        <Badge variant="outline" className="shrink-0 h-9 px-3 font-normal">
                            {groupObjects[0]?.label || field.object}
                        </Badge>
                    )}
                    <div className="flex-1 min-w-0">
                        {loadingObjects[field.object] ? (
                            <div className="flex items-center gap-2 text-sm text-muted-foreground h-9 px-3">
                                <Loader2 className="h-3 w-3 animate-spin" /> Loading properties...
                            </div>
                        ) : (
                            renderPropertySelect({
                                fields: fieldsByObject[field.object] || [],
                                value: field.property || undefined,
                                onSelect: (value: string) => handlePropertySelect(index, value),
                            })
                        )}
                    </div>
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeRow(index)}
                        className="shrink-0 text-muted-foreground hover:text-destructive"
                    >
                        <Trash2 className="h-4 w-4" />
                    </Button>
                </div>
                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 flex-1">
                        <Label className="text-xs text-muted-foreground whitespace-nowrap">Field name</Label>
                        <Input
                            value={field.key}
                            onChange={(e) => updateField(index, { key: slugifyKey(e.target.value) || e.target.value })}
                            placeholder="e.g. plan"
                            className="h-8 text-sm"
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <Switch
                            checked={field.sayable}
                            onCheckedChange={(checked) => updateField(index, { sayable: checked })}
                        />
                        <Label className="text-xs text-muted-foreground flex items-center gap-1 whitespace-nowrap">
                            <Eye className="h-3 w-3" />
                            AI may share this value
                        </Label>
                    </div>
                </div>
            </div>
        );
    };

    const renderFieldGroup = (group: string[], addLabel: string) => {
        const rows = readFields
            .map((field, index) => ({ field, index }))
            .filter(({ field }) => group.includes(field.object));
        return (
            <div className="space-y-3">
                {rows.map(({ field, index }) => renderFieldRow(field, index, group))}
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => addRow(group)}
                    disabled={readFields.length >= MAX_READ_FIELDS}
                    className="w-full border-dashed"
                >
                    <Plus className="h-4 w-4 mr-2" />
                    {readFields.length >= MAX_READ_FIELDS ? `Limit of ${MAX_READ_FIELDS} fields reached` : addLabel}
                </Button>
            </div>
        );
    };

    const contactFieldCount = readFields.filter(
        f => f.property && f.key && CONTACT_GROUP.includes(f.object)
    ).length;
    const accountLabel = provider === 'salesforce' ? 'account' : 'company';
    const dealsLabel = provider === 'salesforce' ? 'open opportunities' : 'open deals';
    const supportsActivity = CRM_ACTIVITY_PROVIDERS.has(provider) && Boolean(setActivityTypes);
    const enabledCount = aiTools.filter(k =>
        ['contact', 'account', 'deals', ...(supportsActivity ? ['activity'] : [])].includes(k)
    ).length;

    const ACTIVITY_TYPE_LABELS: Record<string, string> = {
        notes: 'Notes',
        meetings: 'Meetings',
        calls: 'Calls',
    };
    const toggleActivityType = (type: string, enabled: boolean) => {
        if (!setActivityTypes) return;
        if (enabled) {
            setActivityTypes(activityTypes.includes(type) ? activityTypes : [...activityTypes, type]);
        } else {
            setActivityTypes(activityTypes.filter(t => t !== type));
        }
    };

    return (
        <div className="space-y-4">
            <div className="rounded-xl border bg-card p-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className="font-semibold">AI Tools</h3>
                        <p className="text-sm text-muted-foreground mt-0.5">
                            Choose which CRM lookups the AI can perform. Tools unlock only for
                            identity-verified visitors and can be granted to skills in the Skills tab.
                        </p>
                    </div>
                    {enabledCount > 0 && (
                        <Badge className="bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30">
                            {enabledCount} enabled
                        </Badge>
                    )}
                </div>
            </div>

            {/* Contact record tool */}
            <div className="rounded-xl border p-4 space-y-3">
                <div className="flex items-center justify-between">
                    <div className="flex items-start gap-3">
                        <User className="h-4 w-4 mt-1 text-muted-foreground shrink-0" />
                        <div className="space-y-0.5">
                            <Label>Contact record</Label>
                            <p className="text-sm text-muted-foreground">
                                Let the AI look up the visitor's own contact record. It returns
                                only the fields you map below.
                            </p>
                        </div>
                    </div>
                    <Switch
                        checked={toolEnabled('contact')}
                        onCheckedChange={(checked) => setToolEnabled('contact', checked)}
                    />
                </div>
                {toolEnabled('contact') && (
                    <>
                        {contactFieldCount === 0 && (
                            <p className="text-xs text-amber-600 dark:text-amber-400">
                                Map at least one field — without mapped fields this tool stays inactive.
                            </p>
                        )}
                        {renderFieldGroup(CONTACT_GROUP, 'Add contact field')}
                    </>
                )}
            </div>

            {/* Account tool */}
            <div className="rounded-xl border p-4 space-y-3">
                <div className="flex items-center justify-between">
                    <div className="flex items-start gap-3">
                        <Building2 className="h-4 w-4 mt-1 text-muted-foreground shrink-0" />
                        <div className="space-y-0.5">
                            <Label className="capitalize">{accountLabel}</Label>
                            <p className="text-sm text-muted-foreground">
                                Let the AI look up the visitor's {accountLabel} record. The account
                                owner's name is always included so the AI can tell the visitor who
                                their point of contact is.
                            </p>
                        </div>
                    </div>
                    <Switch
                        checked={toolEnabled('account')}
                        onCheckedChange={(checked) => setToolEnabled('account', checked)}
                    />
                </div>
                {toolEnabled('account') && renderFieldGroup(ACCOUNT_GROUP, `Add ${accountLabel} field`)}
            </div>

            {/* Deals tool */}
            <div className="rounded-xl border p-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-start gap-3">
                        <Handshake className="h-4 w-4 mt-1 text-muted-foreground shrink-0" />
                        <div className="space-y-0.5">
                            <Label className="capitalize">{dealsLabel}</Label>
                            <p className="text-sm text-muted-foreground">
                                Let the AI see the contact's {dealsLabel} (name, stage, amount, close date)
                                as internal context — it will never state amounts to the visitor.
                            </p>
                        </div>
                    </div>
                    <Switch
                        checked={toolEnabled('deals')}
                        onCheckedChange={(checked) => setToolEnabled('deals', checked)}
                    />
                </div>
            </div>

            {/* Recent activity tool (HubSpot only in v1) */}
            {supportsActivity && (
                <div className="rounded-xl border p-4 space-y-3">
                    <div className="flex items-center justify-between">
                        <div className="flex items-start gap-3">
                            <History className="h-4 w-4 mt-1 text-muted-foreground shrink-0" />
                            <div className="space-y-0.5">
                                <Label>Recent activity</Label>
                                <p className="text-sm text-muted-foreground">
                                    Let the AI see your team's recent notes, meetings, and calls with
                                    the contact as internal context — it can reference who they spoke
                                    with and the general topic, but will never quote or reveal note
                                    contents.
                                </p>
                            </div>
                        </div>
                        <Switch
                            checked={toolEnabled('activity')}
                            onCheckedChange={(checked) => setToolEnabled('activity', checked)}
                        />
                    </div>
                    {toolEnabled('activity') && (
                        <div className="space-y-2">
                            {activityTypes.length === 0 && (
                                <p className="text-xs text-amber-600 dark:text-amber-400">
                                    Select at least one activity type — with none selected this tool
                                    stays inactive.
                                </p>
                            )}
                            <div className="flex items-center gap-4">
                                {CRM_ACTIVITY_TYPES.map((type) => (
                                    <label key={type} className="flex cursor-pointer items-center gap-2">
                                        <Checkbox
                                            checked={activityTypes.includes(type)}
                                            onCheckedChange={(checked) => toggleActivityType(type, Boolean(checked))}
                                        />
                                        <span className="text-sm">{ACTIVITY_TYPE_LABELS[type] || type}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
